/* eslint-disable @typescript-eslint/no-explicit-any */
import { flow, FlowCancellationError } from 'mobx';
import { CancellablePromise } from 'mobx/dist/internal';

export function wait(time: number): Promise<void> {
	return new Promise((resolve) => {
		setTimeout(() => {
			resolve();
		}, time);
	});
}

export function* toGenerator<T>(p: T) {
	return (yield p) as T extends Promise<infer G> ? G : T;
}

export function extractErrorMessage(err: unknown) {
	switch (typeof err) {
		case 'string':
			return err;
		case 'object':
			if (err && 'message' in err && err.message) {
				return unknownToString(err.message);
			}
	}
	return;
}

export function unknownToString(originMessage: unknown): string {
	switch (typeof originMessage) {
		case 'undefined':
			return 'undefined';
		case 'string':
			return originMessage;
		case 'bigint':
		case 'function':
		case 'symbol':
		case 'boolean':
		case 'number':
			return originMessage.toString();
		default:
			return JSON.stringify(originMessage);
	}
}

export const isBrowser = !!(typeof window !== 'undefined' && window.document && window.document.createElement);

export const isMobile = () => {
	if (typeof window !== 'undefined') {
		return Boolean(
			(window.matchMedia &&
				typeof window.matchMedia === 'function' &&
				window.matchMedia('(pointer:coarse)')?.matches) ||
				/Android|webOS|iPhone|iPad|iPod|BlackBerry|Opera Mini/u.test(navigator.userAgent),
		);
	}
	return false;
};

function flowCancelCatcher<R>(this: Promise<R>, e: unknown) {
	if (!(e instanceof FlowCancellationError)) {
		throw e;
	}
	return this;
}

/**
 * Similar to flow but cancels already running flow
 */
export function flowSingle<R, Args extends any[]>(
	generator: (...args: Args) => Generator<any, R, any> | AsyncGenerator<any, R, any>,
): ((...args: Args) => Promise<R>) & { cancel: () => void } {
	let promise: CancellablePromise<R> | null;

	const original = flow(generator);
	function fn(this: any, ...args: Args) {
		fn.cancel();
		promise = original.apply(this, args);
		return promise.catch<R>(flowCancelCatcher).finally(() => {
			promise = null;
		});
	}
	fn.cancel = () => {
		if (!promise) return;
		try {
			promise?.cancel();
		} catch (e) {
			flowCancelCatcher.call(promise, e);
		}
	};
	fn.isMobXFlow = true;

	return fn;
}

/**
 * Similar to flow but doesn't call again when it's already loading
 */
export function flowLoading<R, Args extends any[]>(
	generator: (...args: Args) => Generator<any, R, any> | AsyncGenerator<any, R, any>,
): ((...args: Args) => Promise<R>) & { cancel: () => void } {
	let promise: CancellablePromise<R> | null;

	const original = flow(generator);
	function fn(this: any, ...args: Args) {
		if (promise) {
			return promise;
		}
		promise = original.apply(this, args);
		return promise.catch<R>(flowCancelCatcher).finally(() => {
			promise = null;
		});
	}
	fn.cancel = () => {
		if (!promise) return;
		try {
			promise?.cancel();
		} catch (e) {
			flowCancelCatcher.call(promise, e);
		}
	};
	fn.isMobXFlow = true;

	return fn;
}